import { Card, Stack, Group, Text, Button } from '@repletoryreach/sdk-ui';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { Link } from 'react-router-dom';
import { useT } from '../../../i18n/I18nProvider';

/**
 * Where the developer pastes the key the examples and "Try it" should use.
 *
 * The value lives in React state and nowhere else. It is never written to
 * `localStorage`, never written to `sessionStorage`, never sent to our servers
 * and never put in a URL: the only place it goes is the `X-Api-Key` header of a
 * request the browser makes straight to the API. Closing the tab loses it,
 * which is correct, because a key is shown once at creation and we could not
 * put it back even if we wanted to. The copy next to the field says all of this
 * plainly, because a developer deciding whether to paste a live credential into
 * a web page deserves to know rather than to trust.
 */
export function KeyField({
  apiKey,
  onChange
}: {
  apiKey: string;
  onChange: (value: string) => void;
}) {
  const t = useT();
  const looksWrong = apiKey.trim().length > 0 && !apiKey.trim().startsWith('rk_');

  return (
    <Card withBorder className="rr-dev__keycard">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
          <Text role="subtitle">{t('developers.key.title')}</Text>
          <Link to="/settings/api-keys">{t('developers.key.manage')}</Link>
        </Group>

        {/* Not masked. The same key is printed in every curl example further
            down the page, so hiding it in the one field it was typed into would
            be theatre, and it would stop a developer checking they pasted the
            right one. */}
        <TextInput
          label={t('developers.key.label')}
          placeholder="rk_live_..." /* i18n-ignore: the literal prefix every key has */
          value={apiKey}
          onChange={(e) => onChange(e.currentTarget.value)}
          autoComplete="off"
          spellCheck={false}
          error={looksWrong ? t('developers.key.wrongPrefix') : undefined}
          description={t('developers.key.hint')}
        />

        {apiKey.trim().length > 0 && (
          <Group gap="sm" wrap="wrap">
            <Text role="caption" c="var(--rr-color-success)">
              {t('developers.key.inUse')}
            </Text>
            <Button size="xs" variant="subtle" onClick={() => onChange('')}>
              {t('developers.key.forget')}
            </Button>
          </Group>
        )}
      </Stack>
    </Card>
  );
}
